import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Box, Button, ClickAwayListener, IconButton, Paper, Popper, Portal, Slider, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { MIX_CHANNELS, MIX_SILENCE_DB, MIX_MAX_DB, formatMixDb, getMixChannel, getMixVersion, subscribeMix, previewMixChannel, commitMixChannel, resetMix } from '../utils/playbackMix';
import { TOGGLE_MIXER_EVENT } from '../utils/desktopShortcuts';

const SECTION_LABEL_SX = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'rgba(255, 255, 255, 0.45)',
  mb: 1,
};

export default function PlaybackMixer({ buttonSx = {}, centered = false }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  useSyncExternalStore(subscribeMix, getMixVersion, getMixVersion);
  const openRef = useRef(false);
  openRef.current = open;
  const close = () => {
    MIX_CHANNELS.forEach(({ id }) => commitMixChannel(id));
    setOpen(false);
    buttonRef.current?.focus();
  };
  // Raccourci V du navigateur de bureau. Rien d'ecoute hors mise en page bureau,
  // seule celle-ci rend le mixer centre.
  useEffect(() => {
    if (!centered) return;
    const onToggle = () => {
      if (openRef.current) close();
      else setOpen(true);
    };
    window.addEventListener(TOGGLE_MIXER_EVENT, onToggle);
    return () => window.removeEventListener(TOGGLE_MIXER_EVENT, onToggle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centered]);
  // Echap ferme le mixer centre meme quand le focus est retourne a la page
  useEffect(() => {
    if (!centered || !open) return;
    const onKeyDown = event => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centered, open]);
  const buttonBg = centered && open ? 'rgba(var(--accent-rgb), 0.18)' : 'var(--surface-2)';
  const panel = (
        <Paper elevation={8} id="playback-mixer" role="region" aria-label="Mixer volumes"
          onKeyDown={event => {
            // V referme le mixer centre depuis l'interieur : le focus y est apres
            // une ouverture au clavier, le raccourci global ne le verrait pas.
            const toggleKey = centered && event.key?.toLowerCase() === 'v'
              && !event.ctrlKey && !event.metaKey && !event.altKey;
            if (event.key === 'Escape' || toggleKey) { event.stopPropagation(); close(); }
          }}
          sx={{ width: centered ? 360 : 330, maxWidth: 'calc(100vw - 24px)',
            maxHeight: centered ? 'calc(100dvh - 48px)' : 'calc(100dvh - 100px)',
            overflowY: 'auto', p: 2, boxSizing: 'border-box', bgcolor: '#15151A',
            backgroundImage: 'none',
            ...(centered ? { pointerEvents: 'auto' } : { border: '1px solid rgba(255,255,255,0.08)' }),
            borderRadius: '14px',
            color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography component="h2" sx={{ ...SECTION_LABEL_SX, mb: 0 }}>Mixer</Typography>
            <IconButton autoFocus size="small" aria-label="Close mixer" onClick={close}
              sx={{ mr: -0.5, color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.1)' } }}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          {MIX_CHANNELS.map(({ id, label }) => {
            const { db, muted } = getMixChannel(id);
            return (
              <Box key={id} sx={{ display: 'grid', gridTemplateColumns: '1fr 30px', columnGap: 1.5, minHeight: 72, ...(id === 'master' && { mt: 2 }), alignItems: 'center' }}>
                <Box sx={{ gridColumn: 1, display: 'flex', justifyContent: 'space-between', alignSelf: 'end' }}>
                  <Typography id={`mix-${id}`} sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.9)' }}>{formatMixDb(db)}</Typography>
                </Box>
                <Box sx={{ display: 'contents' }}>
                  <Slider aria-labelledby={`mix-${id}`} value={db} min={MIX_SILENCE_DB} max={MIX_MAX_DB} step={1}
                    marks={[{ value: 0, label: '0' }]}
                    getAriaValueText={value => `${formatMixDb(value)}${muted ? ', muted' : ''}`}
                    onChange={(_, value) => previewMixChannel(id, { db: value })}
                    onChangeCommitted={(_, value) => commitMixChannel(id, { db: value })}
                    sx={{ gridColumn: 1, alignSelf: 'start', ml: 0.5, width: 'calc(100% - 8px)',
                      color: muted ? 'rgba(255, 255, 255, 0.4)' : '#fff',
                      '& .MuiSlider-thumb': { backgroundColor: 'currentColor' },
                      '& .MuiSlider-track': { backgroundColor: 'currentColor' },
                      '& .MuiSlider-rail': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                      '& .MuiSlider-mark': { width: 2, height: 8 },
                      '& .MuiSlider-markLabel': { top: 24, fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)' } }} />
                  <IconButton aria-label={`${muted ? 'Unmute' : 'Mute'} ${label}`} aria-pressed={muted}
                    onClick={() => commitMixChannel(id, { muted: !muted })}
                    size="small"
                    sx={{ gridColumn: 2, gridRow: '1 / 3', width: 30, height: 30,
                      color: muted ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.1)' } }}>
                    {muted ? <VolumeOffIcon sx={{ fontSize: 18 }} /> : <VolumeUpIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Box>
              </Box>
            );
          })}
          <Button onClick={resetMix} sx={{ minHeight: 44, p: 0, textTransform: 'none', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.1)' } }}>Reset levels</Button>
        </Paper>
  );
  return (
    <>
      <Button
        ref={buttonRef}
        aria-label="Mixer"
        aria-expanded={open}
        aria-controls={open ? 'playback-mixer' : undefined}
        onClick={() => open ? close() : setOpen(true)}
        sx={{
          pointerEvents: 'auto', minWidth: 55, width: 55, height: 55, p: '8px',
          borderRadius: '28px', textTransform: 'none', color: open ? 'var(--accent)' : 'var(--text-primary, #eee)',
          bgcolor: buttonBg,
          ...(centered ? {} : { border: '1px solid', borderColor: open ? 'var(--accent)' : 'rgba(255,255,255,0.14)' }),
          '&:hover': { bgcolor: buttonBg },
          ...buttonSx,
        }}
      >
        <TuneIcon sx={{ fontSize: 23 }} />
      </Button>
      {centered ? (open && (
        <Portal>
          <ClickAwayListener mouseEvent="onMouseDown"
            onClickAway={event => { if (buttonRef.current?.contains(event.target)) return; close(); }}>
            <Box sx={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', pointerEvents: 'none', zIndex: 1450 }}>
              {panel}
            </Box>
          </ClickAwayListener>
        </Portal>
      )) : (
        <Popper open={open} anchorEl={buttonRef.current} placement="top-end"
          modifiers={[{ name: 'offset', options: { offset: [0, 12] } }, { name: 'preventOverflow', options: { padding: 12 } }]}
          sx={{ zIndex: 1450 }}>
          {panel}
        </Popper>
      )}
    </>
  );
}
